import type { ChangeEvent } from 'react';
import Help from '@mui/icons-material/Help';
import { TableCell as MaterialTableCell, TableSortLabel as MaterialTableSortLabel } from '@mui/material';

import { Tooltip } from 'ui/atoms';
import type { Column, PinnedCellLayout, Row } from 'ui/organisms';

interface Props<T> {
  selected: string[];
  rows: Row<T>[];
  column: Column<T>;
  columnsLength: number;
  orderBy: keyof T;
  order: 'asc' | 'desc';
  handleSelectAllClick: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  handleSort: (event: React.MouseEvent<unknown>, property: keyof T) => void;
  pinned?: PinnedCellLayout;
}

export const TableHeaderCell = <T,>(props: Props<T>) => {
  const createSortHandler = (property: keyof T) => (event: React.MouseEvent<unknown>) => {
    props.handleSort(event, property);
  };
  return (
    <MaterialTableCell
      align={props.column.align ? props.column.align : 'left'}
      padding={'none'}
      className={props.pinned ? 'gcv-table-cell--pinned' : undefined}
      sortDirection={props.orderBy === props.column.id ? props.order : false}
      sx={{
        width: props.column.widthPercent
          ? props.column.widthPercent + '%'
          : props.column.widthEven
          ? 100 / props.columnsLength + '%'
          : 'auto',
        minWidth: props.column.minWidth ? `${props.column.minWidth}px` : undefined,
        height: '40px',
        p: 0,
        pl: '18px',
        color: 'text.secondary',
        // padding: '7px 18px',
        fontWeight: 'bold',
        '& .Mui-active': {
          color: 'text.primary'
        },
        ...(props.pinned && {
          position: 'sticky',
          left: `${props.pinned.left}px`,
          zIndex: 7,
          bgcolor: 'background.secondary',
          boxShadow: props.pinned.isLast ? '2px 0 6px rgba(36, 40, 52, 0.06)' : undefined,
          // Pinned cells fix at minWidth so sticky offsets stay deterministic.
          ...(props.column.minWidth && {
            width: `${props.column.minWidth}px`,
            maxWidth: `${props.column.minWidth}px`
          })
        })
      }}
    >
      {props.column.noSort ? (
        props.column.tooltip ? (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {props.column.label}
            &nbsp;
            <Tooltip title={props.column.tooltip}>
              <Help color="primary" sx={{ fontSize: 16 }} />
            </Tooltip>
          </span>
        ) : (
          props.column.label
        )
      ) : (
        <MaterialTableSortLabel
          active={props.orderBy === props.column.id}
          direction={props.orderBy === props.column.id ? props.order : 'asc'}
          onClick={createSortHandler(props.column.id)}
          sx={{
            lineHeight: '16px',
            '& .MuiTableSortLabel-icon': {
              height: '14px'
            }
          }}
        >
          {props.column.tooltip ? (
            <>
              {props.column.label}
              &nbsp;
              <Tooltip title={props.column.tooltip}>
                <Help color="primary" />
              </Tooltip>
            </>
          ) : (
            props.column.label
          )}
        </MaterialTableSortLabel>
      )}
    </MaterialTableCell>
  );
};
